import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Pencil, Trash, Plus, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import ImageUpload from "../../components/ImageUpload";

const empty = {
  name: { en: "", gr: "" },
  category: "speakers",
  short_desc: { en: "", gr: "" },
  description: { en: "", gr: "" },
  image: "", brand: "", sku: "", price_text: "", spec_url: "",
  featured: false, in_stock: true,
  specs: [],
};

const CATS = ["speakers", "amplifiers", "mixers", "microphones", "lighting", "stage", "video", "cctv"];

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/products").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...editing,
      specs: typeof editing.specs === "string" ? editing.specs.split("\n").filter(Boolean) : (editing.specs || []),
    };
    try {
      if (editing.id) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Saved"); setEditing(null); load();
    } catch { toast.error("Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between mb-8">
        <h1 className="display-font text-3xl font-black uppercase">Products</h1>
        <button onClick={() => setEditing({ ...empty })} className="btn-primary" data-testid="new-product-btn">
          <Plus size={18} weight="bold" /> New
        </button>
      </div>

      <div className="grid gap-3">
        {items.map((p) => (
          <div key={p.id} className="card-dark p-4 flex items-center gap-4" data-testid={`admin-product-${p.id}`}>
            <div className="w-16 h-16 bg-[#0a0a0c] shrink-0 overflow-hidden">{p.image && <img src={p.image} alt="" className="w-full h-full object-cover" />}</div>
            <div className="flex-1">
              <div className="display-font font-bold">{p.name.en || p.name.gr}</div>
              <div className="mono-font text-xs text-gray-500 mt-1">{p.brand} · {p.category} · {p.price_text}</div>
            </div>
            <button onClick={() => setEditing({ ...p, specs: (p.specs || []).join("\n") })} className="p-2 hover:text-[#ccff00]" data-testid={`edit-${p.id}`}><Pencil size={18} /></button>
            <button onClick={() => del(p.id)} className="p-2 hover:text-[#ff003c]" data-testid={`del-${p.id}`}><Trash size={18} /></button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="product-modal">
          <form onSubmit={save} className="bg-[#0a0a0c] border border-[#2a2a35] max-w-3xl w-full max-h-[90vh] overflow-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="display-font text-2xl font-bold">{editing.id ? "Edit" : "New"} product</h2>
              <button type="button" onClick={() => setEditing(null)} className="p-2"><X size={20} /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input required placeholder="Name (EN)" className="input-dark" value={editing.name.en} onChange={(e) => setEditing({ ...editing, name: { ...editing.name, en: e.target.value } })} />
              <input placeholder="Name (GR)" className="input-dark" value={editing.name.gr} onChange={(e) => setEditing({ ...editing, name: { ...editing.name, gr: e.target.value } })} />
              <select className="input-dark" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}>
                {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input placeholder="Brand" className="input-dark" value={editing.brand} onChange={(e) => setEditing({ ...editing, brand: e.target.value })} />
              <input placeholder="SKU" className="input-dark" value={editing.sku} onChange={(e) => setEditing({ ...editing, sku: e.target.value })} />
              <input placeholder="Price text" className="input-dark" value={editing.price_text} onChange={(e) => setEditing({ ...editing, price_text: e.target.value })} />
              <input placeholder="Spec sheet URL (manufacturer page)" className="input-dark sm:col-span-2" value={editing.spec_url || ""} onChange={(e) => setEditing({ ...editing, spec_url: e.target.value })} data-testid="product-spec-url" />
              <div className="sm:col-span-2">
                <ImageUpload value={editing.image} onChange={(url) => setEditing({ ...editing, image: url })} label="Product image" />
              </div>
              <input placeholder="Short desc (EN)" className="input-dark" value={editing.short_desc.en} onChange={(e) => setEditing({ ...editing, short_desc: { ...editing.short_desc, en: e.target.value } })} />
              <input placeholder="Short desc (GR)" className="input-dark" value={editing.short_desc.gr} onChange={(e) => setEditing({ ...editing, short_desc: { ...editing.short_desc, gr: e.target.value } })} />
              <textarea placeholder="Description (EN)" className="input-dark min-h-[100px]" value={editing.description.en} onChange={(e) => setEditing({ ...editing, description: { ...editing.description, en: e.target.value } })} />
              <textarea placeholder="Description (GR)" className="input-dark min-h-[100px]" value={editing.description.gr} onChange={(e) => setEditing({ ...editing, description: { ...editing.description, gr: e.target.value } })} />
              <textarea placeholder="Specs (one per line)" className="input-dark sm:col-span-2 min-h-[100px]" value={editing.specs} onChange={(e) => setEditing({ ...editing, specs: e.target.value })} />
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} /> Featured</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.in_stock} onChange={(e) => setEditing({ ...editing, in_stock: e.target.checked })} /> In stock</label>
            </div>
            <div className="mt-6 flex gap-3 justify-end">
              <button type="button" onClick={() => setEditing(null)} className="btn-outline">Cancel</button>
              <button type="submit" className="btn-primary" data-testid="save-product">Save</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
